import type { ReactNode } from 'react'
import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Button, IconButton, cx } from './core.tsx'

/**
 * Fecha no Esc e prende o foco dentro do overlay enquanto ele estiver aberto.
 *
 * Sem o trap, o Tab escapa para a pagina atras do modal — o leitor de tela
 * continua lendo conteudo que visualmente esta coberto.
 */
function useOverlayBehavior(open: boolean, onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null)
  // onClose costuma ser uma arrow recriada a cada render do chamador; se ela
  // entrar nas deps do efeito, ele re-executa a CADA tecla digitada e o
  // .focus() inicial rouba o foco do campo. O ref desacopla isso.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!open) return
    const previouslyFocused = document.activeElement as HTMLElement | null

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onCloseRef.current()
        return
      }
      if (event.key !== 'Tab' || !ref.current) return
      const focusables = ref.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      if (!first || !last) return
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    ref.current?.querySelector<HTMLElement>('button, input, [tabindex]')?.focus()

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = overflow
      previouslyFocused?.focus()
    }
  }, [open])

  return ref
}

export type OverlayProps = {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  footer?: ReactNode
}

// ------------------------------------------------------------------ Drawer

export type DrawerProps = OverlayProps & { side?: 'right' | 'left'; width?: number }

/**
 * Painel lateral para acoes rapidas (spec 7.3): criar, editar e detalhar sem
 * perder o contexto da lista atras. Fluxos longos vao para pagina dedicada.
 */
export function Drawer({ open, onClose, title, children, footer, side = 'right', width }: DrawerProps) {
  const ref = useOverlayBehavior(open, onClose)
  if (!open) return null

  return createPortal(
    <>
      <div className="ob-overlay" onClick={onClose} aria-hidden />
      <div
        ref={ref}
        className={cx('ob-drawer', side === 'left' && 'ob-drawer--left')}
        style={width ? { width } : undefined}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header className="ob-overlay__header">
          <h2 className="ob-overlay__title">{title}</h2>
          <IconButton icon="fas fa-times" label="Fechar" onClick={onClose} />
        </header>
        <div className="ob-overlay__body">{children}</div>
        {footer ? <footer className="ob-overlay__footer">{footer}</footer> : null}
      </div>
    </>,
    document.body,
  )
}

// ------------------------------------------------------------------- Modal

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  width,
  resizable,
  headerExtra,
  sizeKey,
  titleNode,
}: OverlayProps & {
  width?: number
  /** Deixa a janela redimensionavel pelo canto (grip do CSS `resize`). */
  resizable?: boolean
  /**
   * Lembra o tamanho que o usuario deixou (2026-09-01): aplica ao abrir e
   * grava no localStorage a cada redimensionamento. So faz sentido com
   * `resizable`.
   */
  sizeKey?: string
  /** Substitui o TEXTO do titulo por um no proprio (ex.: titulo editavel);
      `title` continua obrigatorio para o aria-label. */
  titleNode?: ReactNode
  /** Acoes extras no cabecalho, ANTES do X (ex.: expandir para tela cheia). */
  headerExtra?: ReactNode
}) {
  const ref = useOverlayBehavior(open, onClose)
  // aplica o tamanho lembrado e grava o que o usuario deixar
  useEffect(() => {
    if (!open || !sizeKey || !ref.current) return
    const el = ref.current
    try {
      const bruto = localStorage.getItem(`ob-modal-size:${sizeKey}`)
      if (bruto) {
        const { w, h } = JSON.parse(bruto) as { w?: number; h?: number }
        if (w) el.style.width = `min(${Math.round(w)}px, calc(100vw - 24px))`
        if (h) el.style.height = `min(${Math.round(h)}px, calc(100vh - 24px))`
      }
    } catch {
      // storage invalido: segue com o tamanho padrao
    }
    // a 1a medida do observer e o tamanho inicial — so grava a partir da 2a
    let primeira = true
    let t: number | undefined
    const ro = new ResizeObserver(() => {
      if (primeira) { primeira = false; return }
      window.clearTimeout(t)
      t = window.setTimeout(() => {
        const r = el.getBoundingClientRect()
        try {
          localStorage.setItem(`ob-modal-size:${sizeKey}`, JSON.stringify({ w: Math.round(r.width), h: Math.round(r.height) }))
        } catch {
          // sem espaco no storage: paciencia
        }
      }, 250)
    })
    ro.observe(el)
    return () => { window.clearTimeout(t); ro.disconnect() }
  }, [open, sizeKey, ref])
  if (!open) return null

  return createPortal(
    <>
      <div className="ob-overlay" onClick={onClose} aria-hidden />
      <div
        ref={ref}
        className={`ob-modal${resizable ? ' ob-modal--resizable' : ''}`}
        style={width ? { width: `min(${width}px, calc(100vw - 32px))` } : undefined}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header className="ob-overlay__header">
          <h2 className="ob-overlay__title">{titleNode ?? title}</h2>
          {headerExtra}
          <IconButton icon="fas fa-times" label="Fechar" onClick={onClose} />
        </header>
        <div className="ob-overlay__body">{children}</div>
        {footer ? <footer className="ob-overlay__footer">{footer}</footer> : null}
      </div>
    </>,
    document.body,
  )
}

// ----------------------------------------------------------- ConfirmDialog

export type ConfirmDialogProps = {
  open: boolean
  title: string
  /** Explica o impacto real. "Tem certeza?" sozinho nao informa nada. */
  description: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  destructive?: boolean
  loading?: boolean
  onConfirm: () => void
  onCancel: () => void
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  destructive = false,
  loading = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button variant={destructive ? 'danger' : 'primary'} onClick={onConfirm} loading={loading}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div style={{ fontSize: 13, color: 'var(--ob-text-subtle)' }}>{description}</div>
    </Modal>
  )
}
